/**
 * @file cabinet-page-header-parts.tsx
 * @description Части заголовка страницы кабинета (phase19.31, шаг О4):
 * кнопка «назад», кнопка второстепенного действия, меню «⋯» и закреплённая
 * нижняя полоса. Собирает их `CabinetPageHeader` (название-h1 живёт в нём:
 * линтер контракта разрешает h1 только владельцу заголовка).
 *
 * Высоты и шрифты кнопок здесь не задаются: их даёт тема по `data-slot`/
 * `data-size` (48 до 1024, 44 с 1024, 56 у кладовщика до 1024).
 */

import { ArrowLeft, MoreHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { ICON_ONLY_LABEL_CLASS, orderPageActions, type PageAction } from "./page-actions";
import { STICKY_BAR_CLASS, stickyBarProps } from "./sticky-bar";
import { cn } from "@/lib/utils";

/** Кнопка «назад» слева от названия страницы. */
export function PageBackButton({ to, label }: { to: string; label: string }) {
  return (
    <Button asChild variant="ghost" size="icon" className="shrink-0">
      <Link to={to} aria-label={label} title={label}>
        <ArrowLeft className="size-5" />
      </Link>
    </Button>
  );
}

/** Номер документа склада: «PICK-2026-000012», «RCP-2026-000018-1». */
const DOC_CODE = /^([A-ZА-ЯЁ]{1,8})-(\d{4})-(\d+(?:-\d+)*)$/;

/** Номер в сжатом заголовке — на ступень меньше (18px вместо 20). */
const DOC_CODE_STYLE = { fontSize: "1.125rem" } as const;

/**
 * Текст сжатого заголовка (телефон, планшет). Номер документа (дефект
 * приёмки 2026-10-02: на 360 «PICK-2026-…» обрывался многоточием — номер
 * переносился по обоим дефисам и не влезал в две строки): первый дефис
 * неразрывный, перенос — только после года («PICK-2026-» / «000012»), кегль
 * на ступень меньше — первая половина помещается рядом с «назад» и «⋯».
 * Остальные названия — как есть.
 */
export function DocCodeText({ title }: { title: string }) {
  const match = DOC_CODE.exec(title);
  if (!match) return <>{title}</>;
  return (
    <span style={DOC_CODE_STYLE}>
      {match[1]}
      {"‑"}
      {match[2]}-<wbr />
      {match[3]}
    </span>
  );
}

/** Второстепенное действие кнопкой-контуром (широкий экран). */
export function PageActionButton({ action }: { action: PageAction }) {
  const Icon = action.icon;
  const content = (
    <>
      {Icon ? <Icon className="size-4" /> : null}
      {action.iconOnlyBelow && Icon ? <span className={ICON_ONLY_LABEL_CLASS[action.iconOnlyBelow]}>{action.label}</span> : action.label}
    </>
  );
  if (action.to && !action.disabled) {
    return (
      <Button asChild variant={action.danger ? "destructive" : "outline"}>
        <Link to={action.to}>{content}</Link>
      </Button>
    );
  }
  return (
    <Button
      variant={action.danger ? "destructive" : "outline"}
      onClick={action.run}
      disabled={action.disabled}
      loading={action.loading}
      title={action.hint}
      aria-description={action.hint}
    >
      {content}
    </Button>
  );
}

interface MenuProps {
  actions: PageAction[];
  /** Подпись кнопки «⋯» для скринридера. */
  label: string;
}

/** Меню «⋯» страницы: обычные действия, затем опасные после линии. */
export function PageActionsMenu({ actions, label }: MenuProps) {
  const navigate = useNavigate();
  const ordered = orderPageActions(actions);
  const firstDanger = ordered.findIndex((a) => a.danger);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={(props) => (
          <Button {...props} variant="outline" size="icon" aria-label={label} title={label}>
            <MoreHorizontal className="size-5" />
          </Button>
        )}
      />
      <DropdownMenuContent align="end" sideOffset={6} className="min-w-64">
        {ordered.map((action, index) => {
          const Icon = action.icon;
          return (
            <MenuEntry key={action.key} separated={index === firstDanger && index > 0}>
              <DropdownMenuItem
                disabled={action.disabled || action.loading}
                className={action.danger ? "text-destructive" : undefined}
                onClick={() => (action.to ? navigate(action.to) : action.run?.())}
              >
                {Icon ? <Icon /> : null}
                {action.label}
              </DropdownMenuItem>
            </MenuEntry>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Пункт меню с линией перед первым опасным действием. */
function MenuEntry({ separated, children }: { separated: boolean; children: ReactNode }) {
  return (
    <>
      {separated ? <DropdownMenuSeparator /> : null}
      {children}
    </>
  );
}

interface BarProps {
  children: ReactNode;
  /** Подпись над кнопкой: «Обновлено в 14:32». */
  note?: ReactNode;
  /** Порог каркаса: полоса прячется стилями с этой ширины (страховка от мигания). */
  hideFrom: "md" | "lg";
}

/**
 * Общая нижняя полоса каркаса: одна на экран, место под неё резервирует
 * обёртка содержимого по маркеру `data-cabinet-bottom-action`, поэтому полоса
 * ничего не накрывает.
 */
export function CabinetBottomBar({ children, note, hideFrom }: BarProps) {
  return (
    <div
      {...stickyBarProps}
      data-cabinet-bottom-action=""
      className={cn(STICKY_BAR_CLASS, hideFrom === "md" && "md:hidden", "[&>*]:w-full")}
    >
      {note ? <p className="type-caption text-center text-on-surface-variant">{note}</p> : null}
      {children}
    </div>
  );
}
